import { Tooltip } from "antd";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { IPatient, PatientListTableContent } from "../../../../@types/Patient";
import { IGetPatientAdherenceDataResponse } from "../../../../@types/Treatments";
import { AlertsCounter } from "../../../../components/AlertsCounter";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { useFlagPatient } from "../../../../hooks/mutations/patients";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { treatmentsService } from "../../../../services/treatmentsService";
import {
  getLongFormattedDate,
  getRelativeTimeSinceDate,
  getShortFormattedDate,
} from "../../../../utils/dateFormatter";
import { PatientListSortedTable } from "../../../PatientList/components/PatientListSortedTable";
import { FlagPatientButton } from "../../../PatientList/components/PatientListSortedTable/components/FlagPatientButton";
import {
  AdherenceCategory,
  AdherenceGraph,
} from "../AdherenceGraph/AdherenceGraph";
import {
  AdherenceContainer,
  Container,
  DayButton,
  DayNavigator,
  Divider,
} from "./AdherenceGraphContainer.styles";
import { adherenceTableColumns } from "./adherenceTableColumns";

dayjs.extend(isToday);

export interface IAdherenceGraphContainerProps {
  data: IGetPatientAdherenceDataResponse;
  isLoading: boolean;
}

export function AdherenceGraphContainer({
  data,
  isLoading,
}: IAdherenceGraphContainerProps) {
  const navigate = useNavigate();
  const mutate = useFlagPatient();
  const { t } = useTranslation();

  const [patients, setPatients] = useState<IPatient[]>([]);
  const [adherenceData, setAdherenceData] =
    useState<IGetPatientAdherenceDataResponse>(data);
  const [adherenceDate, setAdherenceDate] = useState(data.date);
  const [isRefetching, setIsRefetching] = useState(false);
  const [hoveredType, setHoveredType] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const inFlightRef = useRef(false);

  const getPatientCategory = useCallback(
    (p: IPatient): AdherenceCategory | null => {
      return (p as any).adherenceCategory ?? null;
    },
    []
  );

  useEffect(() => {
    try {
      const parsedPatients = JSON.parse(
        adherenceData.patientsJson
      ) as IPatient[];

      setPatients(parsedPatients);
    } catch (error) {
      console.error(error);
    }
  }, [adherenceData]);

  const parsedContent = useMemo(() => {
    // EN-5191: derived from the patient record, not from the rendered label. This used to
    // compare against the English strings below, so translating them would have silently
    // reordered the table instead of failing.
    const getLastUsedDate = (lastUsed: any, isPasswordSet: boolean) => {
      if (lastUsed !== null) return dayjs(lastUsed).valueOf();
      return isPasswordSet ? 1 : 0;
    };

    const getAlertsCount = (alerts: string) => {
      const parsedAlerts = Number(alerts?.toString());
      if (Number.isNaN(parsedAlerts)) {
        return "1";
      }
      return alerts?.toString();
    };

    const handleFlagPatient = async (patientId: number, flagged: boolean) => {
      await mutate({
        patientId,
        flagged,
      });

      setPatients((prev) =>
        prev.map((p) => (p.id === patientId ? { ...p, flagged } : p))
      );
    };

    if (patients === undefined || patients.length === 0) return [];

    const patientList = patients.map((patient) => {
      let lastUsedContent = "";

      if (patient.lastUsed !== null) {
        const lastUsedFromatted = getLongFormattedDate(patient.lastUsed);
        lastUsedContent = getRelativeTimeSinceDate(lastUsedFromatted);
      } else if (patient.isPasswordSet === true) {
        lastUsedContent = t("dashboard_noDataAdded");
      } else {
        lastUsedContent = t("dashboard_passwordNotSet");
      }

      return {
        id: patient.id,
        name: `${patient.firstName} ${patient.lastName}`,
        flagged: patient.flagged,
        lastUsed: lastUsedContent,
        lastUsedDate: getLastUsedDate(
          patient.lastUsed,
          patient.isPasswordSet === true
        ),
        hospitalPatientId: patient.hospitalPatientId,
        signUpDate: getLongFormattedDate(patient.signUpDate),
        signUpDateParsedDate: dayjs(patient.signUpDate).valueOf(),
        lastReviewedBy: patient.lastReviewedBy ?? "",
        alertsCount: getAlertsCount(patient.alerts),
        alerts:
          patient.alerts !== "0" ? (
            <Tooltip
              mouseLeaveDelay={0}
              title={t("dashboard_goToAlerts")}
              zIndex={3002}
              color="#4B4B4B"
              arrow={false}
              styles={{
                body: {
                  minHeight: "40px",
                  borderRadius: "4px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                },
              }}
            >
              <CustomIconButton
                style={{
                  width: "max-content",
                  margin: "0 10px 0 0",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/patients/${patient.id}/alerts`);
                }}
              >
                <AlertsCounter>{patient.alerts}</AlertsCounter>
              </CustomIconButton>
            </Tooltip>
          ) : (
            "-"
          ),
        flag: (
          <div>
            <FlagPatientButton
              onClick={async (e) => {
                e.stopPropagation();

                await handleFlagPatient(patient.id, !patient.flagged);
              }}
              flagged={patient.flagged}
            />
          </div>
        ),
        adherenceCategory: patient.adherenceCategory ?? null,
      } as unknown as PatientListTableContent;
    });

    if (selectedType) {
      return patientList.filter(
        (patient) => patient.adherenceCategory === selectedType
      );
    }

    if (hoveredType) {
      const parts = patientList.reduce(
        (acc, patient) => {
          if (patient.adherenceCategory === hoveredType) {
            acc.head.push(patient);
          } else {
            acc.tail.push(patient);
          }
          return acc;
        },
        {
          head: [] as PatientListTableContent[],
          tail: [] as PatientListTableContent[],
        }
      );

      return [...parts.head, ...parts.tail];
    }

    return patientList;
  }, [patients, hoveredType, selectedType, getPatientCategory, t]);

  const handleDayNavigation = useCallback(
    async (isNextDay: boolean) => {
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      setIsRefetching(true);

      try {
        const delta = isNextDay ? 1 : -1;
        const targetDate = dayjs(adherenceDate)
          .add(delta, "day")
          .format("YYYY-MM-DD");

        setAdherenceDate(targetDate);
        setHoveredType(null);
        setSelectedType(null);

        const serverData = await treatmentsService.getPatientsAdherenceData(
          targetDate
        );

        const dayData = serverData.find(
          (d) => d.protocolId === adherenceData.protocolId
        );

        if (dayData) setAdherenceData(dayData);
      } finally {
        inFlightRef.current = false;
        setIsRefetching(false);
      }
    },
    [adherenceDate, adherenceData.protocolId]
  );

  return (
    <Container>
      <PageTitle data-testid="page-title">
        {t("dashboard_adherence_title", {
          protocol: adherenceData.protocolName,
          date: getShortFormattedDate(adherenceDate),
        })}
      </PageTitle>

      <AdherenceContainer>
        <AdherenceGraph
          data-testid="adherence-graph"
          data={adherenceData}
          key={`${adherenceData.protocolId}-${adherenceData.date}`}
          onHoverCategory={setHoveredType}
          onSelectCategory={setSelectedType}
        />

        <DayNavigator>
          <DayButton
            data-testid="previous-button"
            disabled={
              isRefetching ||
              dayjs(adherenceDate).isSame(dayjs(adherenceData.created), "day")
            }
            onClick={() => handleDayNavigation(false)}
          >
            {t("dashboard_adherence_previousDay")}
          </DayButton>
          <Divider />
          <DayButton
            data-testid="next-button"
            disabled={isRefetching || dayjs(adherenceDate).isToday()}
            onClick={() => handleDayNavigation(true)}
          >
            {t("dashboard_adherence_nextDay")}
          </DayButton>
        </DayNavigator>

        <PatientListSortedTable
          variant=""
          isAdherenceList
          isLoading={isLoading}
          content={parsedContent}
          columns={adherenceTableColumns}
          getRowFlags={(record) => {
            const isHoveringOnly = Boolean(hoveredType && !selectedType);
            const isOtherCategory = record.adherenceCategory !== hoveredType;
            return {
              dimmed: isHoveringOnly && isOtherCategory,
            };
          }}
        />
      </AdherenceContainer>
    </Container>
  );
}
